import axios from "axios";
import { API } from "@/lib/api";
import { DEFAULT_LANG } from "@/i18n/languages";

// Translates rendered text nodes + attributes: locale catalog first, then cached AI translation (backend). English is restored on switch-back.
const SKIP = "[data-no-translate], script, style, code, pre, textarea, input, .font-mono, [contenteditable='true']";
const ATTRS = ["placeholder", "title", "aria-label"];
const NEEDS = /[A-Za-z]{2,}/;
const NOISE = /^(?:[A-Z]{2,5}-[\w-]+|[\w.+-]+@[\w-]+\.[\w.]+|https?:\/\/\S+|v?\d[\d.,:/\s-]*)$/;
const BATCH = 50;
const MAX_PARALLEL = 3;

class Translator {
  lang = DEFAULT_LANG;
  catalog = {};
  memo = {};
  texts = new Map();
  attrs = new Map();
  queue = new Map();
  inflight = 0;
  observer = null;
  onBusy = () => {};

  setLanguage(lang, catalog, onBusy) {
    this.lang = lang;
    this.catalog = catalog || {};
    this.onBusy = onBusy || (() => {});
    this.queue.clear();
    if (lang === DEFAULT_LANG) { this.stop(); this.restore(); return; }
    try { this.memo = JSON.parse(localStorage.getItem(`dpdp.tx.${lang}`) || "{}"); } catch { this.memo = {}; }
    this.start();
    this.scan(document.body);
  }

  start() {
    if (this.observer) return;
    this.observer = new MutationObserver((ms) => ms.forEach((m) => {
      if (m.type === "characterData") this.text(m.target);
      else if (m.type === "attributes") this.attr(m.target, m.attributeName);
      else m.addedNodes.forEach((n) => this.scan(n));
    }));
    this.observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  }

  stop() { this.observer?.disconnect(); this.observer = null; }

  restore() {
    this.texts.forEach((r, n) => { if (n.isConnected && n.nodeValue === r.out) n.nodeValue = r.src; });
    this.attrs.forEach((m, el) => Object.entries(m).forEach(([a, r]) => { if (el.getAttribute(a) === r.out) el.setAttribute(a, r.src); }));
    this.texts.clear(); this.attrs.clear();
  }

  skip(el) { return !el || el.closest(SKIP); }

  wanted(s) { const t = s.trim(); return t.length > 1 && t.length < 4000 && NEEDS.test(t) && !NOISE.test(t); }

  lookup(s) { const c = this.catalog[s]; return typeof c === "string" ? c : (c?.other ?? this.memo[s]); }

  scan(root) {
    if (root.nodeType === 3) return this.text(root);
    if (root.nodeType !== 1 || this.skip(root)) return;
    ATTRS.forEach((a) => root.hasAttribute(a) && this.attr(root, a));
    const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
    for (let n = w.nextNode(); n; n = w.nextNode()) {
      if (n.nodeType === 3) this.text(n);
      else ATTRS.forEach((a) => n.hasAttribute(a) && this.attr(n, a));
    }
  }

  text(node) {
    if (this.lang === DEFAULT_LANG || this.skip(node.parentElement)) return;
    const v = node.nodeValue || "";
    const rec = this.texts.get(node);
    if (rec && v === rec.out) return;
    if (!this.wanted(v)) return;
    const r = { src: v, out: null };
    this.texts.set(node, r);
    this.resolve(v.trim(), (tr) => {
      if (node.nodeValue !== v) return;
      r.out = v.replace(v.trim(), tr);
      node.nodeValue = r.out;
    });
  }

  attr(el, a) {
    if (this.lang === DEFAULT_LANG || this.skip(el)) return;
    const v = el.getAttribute(a) || "";
    const m = this.attrs.get(el) || {};
    if (m[a] && v === m[a].out) return;
    if (!this.wanted(v)) return;
    m[a] = { src: v, out: null };
    this.attrs.set(el, m);
    this.resolve(v.trim(), (tr) => { if (el.getAttribute(a) === v) { m[a].out = tr; el.setAttribute(a, tr); } });
  }

  resolve(s, apply) {
    const hit = this.lookup(s);
    if (hit) return apply(hit);
    if (!this.queue.has(s)) this.queue.set(s, []);
    this.queue.get(s).push(apply);
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.flush(), 120);
  }

  async flush() {
    while (this.queue.size && this.inflight < MAX_PARALLEL) {
      const batch = [...this.queue.entries()].slice(0, BATCH);
      batch.forEach(([s]) => this.queue.delete(s));
      this.run(batch, this.lang);
    }
  }

  async run(batch, lang) {
    this.inflight += 1; this.onBusy(true);
    try {
      const { data } = await axios.post(`${API}/i18n/translate`, { lang, texts: batch.map(([s]) => s) }, { withCredentials: true });
      if (lang === this.lang) {
        batch.forEach(([s, fns], i) => { const tr = data.translations[i]; this.memo[s] = tr; fns.forEach((f) => f(tr)); });
        this.persist();
      }
    } catch { /* graceful fallback: keep English */ }
    this.inflight -= 1;
    if (this.queue.size) this.flush(); else if (!this.inflight) this.onBusy(false);
  }

  persist() {
    clearTimeout(this.ptimer);
    this.ptimer = setTimeout(() => {
      const keys = Object.keys(this.memo);
      if (keys.length > 4000) keys.slice(0, keys.length - 4000).forEach((k) => delete this.memo[k]);
      try { localStorage.setItem(`dpdp.tx.${this.lang}`, JSON.stringify(this.memo)); } catch { /* quota */ }
    }, 800);
  }
}

export const translator = new Translator();
